'use client'

import { useState, useEffect, useRef } from 'react'
import { useTranslation } from '@/i18n/client'
import { locales, type Locale } from '@/i18n/config'

const FLAG_BASE = 'https://flagcdn.com/'

// Locale → ISO-Ländercode für Flaggen-SVG
const LOCALE_FLAG: Record<Locale, string> = {
  de: 'de', en: 'gb', fr: 'fr', it: 'it', es: 'es', pt: 'pt',
  nl: 'nl', da: 'dk', fi: 'fi', pl: 'pl', sv: 'se', sl: 'si',
  ja: 'jp', 'zh-CN': 'cn', ko: 'kr', th: 'th', he: 'il',
}

const LANGUAGE_NAME: Record<Locale, string> = {
  de: 'Deutsch', en: 'English', fr: 'Français', it: 'Italiano',
  es: 'Español', pt: 'Português', nl: 'Nederlands', da: 'Dansk',
  fi: 'Suomi', pl: 'Polski', sv: 'Svenska', sl: 'Slovenščina',
  ja: '日本語', 'zh-CN': '简体中文', ko: '한국어', th: 'ภาษาไทย', he: 'עברית',
}

function FlagImg({ locale, size = 16 }: { locale: Locale; size?: number }) {
  return (
    <img
      src={`${FLAG_BASE}${LOCALE_FLAG[locale]}.svg`}
      alt={LANGUAGE_NAME[locale]}
      className="rounded-[2px] border border-[var(--border)] object-cover"
      style={{ width: size, height: Math.round(size * 0.7) }}
      loading="lazy"
    />
  )
}

export function LanguageSwitcher() {
  const { locale, setLocale } = useTranslation()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const switchLocale = (newLocale: Locale) => {
    setLocale(newLocale)
    setOpen(false)
  }

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="wertung-tab-pill inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest cursor-pointer"
        data-state={open ? 'active' : 'inactive'}
        title={LANGUAGE_NAME[locale]}
        data-tip={LANGUAGE_NAME[locale]}
      >
        <FlagImg locale={locale} size={16} />
        <span>{locale}</span>
        <span style={{ fontSize: 9, opacity: .7 }}>▼</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 min-w-[170px] max-h-[min(80vh,520px)] overflow-y-auto overscroll-contain rounded-xl border border-[var(--border)] bg-[var(--bg-card)] shadow-lg">
          {locales.map((loc) => (
            <button
              key={loc}
              onClick={() => switchLocale(loc)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-[11px] font-bold tracking-[.04em] hover:bg-[var(--accent)]/10 transition-colors text-left ${
                locale === loc
                  ? 'bg-[var(--accent)]/15 text-[var(--accent)]'
                  : 'text-[var(--text)]'
              }`}
            >
              <FlagImg locale={loc} size={18} />
              <span>{LANGUAGE_NAME[loc]}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
